import { widgetViewAccess } from "@salestrak/widgets";
import { canEdit as accessLevelCanEdit, type AccessLevel } from "#app/lib/utils/access-level";

/**
 * The pure client half of the widget view-ownership decision: grammar access
 * (the SAME shared module the server guard feeds) plus the UX policy layered
 * on top. Pure and sync so the node test harness can truth-table it;
 * `useWidgetViewAccess` resolves the actor inputs from client data and calls
 * this.
 *
 * ONE model for every view: a view has a shared/default board, and any viewer
 * may hold a personal fork of it (a per-user settings snapshot — the server
 * never sees it as widget writes, so it needs no permission). The only
 * permission question left is who may edit the SHARED board: `canWrite`.
 *
 * The one exception is the share-only viewer (`consumeOnly`): a role without
 * public access reaches a shared dashboard only through a share, and there it
 * READS. Whatever level the share names, they get the widgets and nothing to
 * steer them with — no fork, no edit chrome, no transient controls. My Work
 * stays theirs: a private view is not a shared board.
 */

export type WidgetViewTier =
  /** Own `member:` view (My Work) — full direct edit, no permission needed,
   *  no fork (the view already IS personal). */
  | "private-owner"
  /** Any shared view. `canWrite` says whether this viewer may edit the shared
   *  board; personalization is open to every viewer. */
  | "shared"
  /** A shared view whose ROW carries its own ACL — a workspace dashboard.
   *  Behaves as `shared` everywhere (forks, staged saves), but `canWrite`
   *  comes from the row's creator/share level, never from `widgets.manage`. */
  | "acl";

/** The two tiers that hold a shared/default board any viewer may fork. */
export function isSharedTier(tier: WidgetViewTier): boolean {
  return tier === "shared" || tier === "acl";
}

/**
 * The ACL a workspace dashboard's row resolves for the viewer: who made it and
 * the level the server computed for this viewer (`EntityView.viewerAccessLevel`
 * — null when the viewer cannot reach the row at all). The host reads it off
 * the active view row; the policy only compares.
 */
export type WidgetViewAcl = {
  ownerId: string | null;
  viewerAccessLevel: AccessLevel | null;
};

export type WidgetViewPolicyActor = {
  userId: string;
  /** Holds the `widgets.manage` permission. */
  canManageWidgets: boolean;
  /** Manages the given team (`Team.viewerCanManage`: workspace manage.teams
   *  OR a manager of that team) — the shared-write grant (team scope only). */
  managesTeam: (teamId: string) => boolean;
  /** Holds a role without public access (Guest, Limited Member, a custom role
   *  flagged the same) — the share-only tier. */
  shareOnly: boolean;
};

/** WHICH grant made the viewer a shared-board editor — copy-level context so
 *  the UI can say "because you manage this team" instead of a bare "you can
 *  edit". `team-manager` wins when both apply (the more personal reason).
 *  Null when the viewer can't write shared rows (or owns a private view — no
 *  grant concept there). */
export type WidgetViewWriteGrant =
  | "team-manager"
  | "permission"
  /** The viewer made this dashboard. */
  | "owner"
  /** Shared to the viewer (directly, through a team, or public) at a level
   *  that allows `edit`. */
  | "access-level";

export type WidgetViewPolicy = {
  /** Whether writes to the canonical board will be accepted by the server. */
  canWrite: boolean;
  writeGrant: WidgetViewWriteGrant | null;
  tier: WidgetViewTier;
  /** A share-only viewer on a shared board: the widgets render and nothing
   *  else — no fork, no edit chrome, no transient controls (date window,
   *  Only Me, filter chip, module header actions). Never true on the
   *  viewer's own private view. */
  consumeOnly: boolean;
};

export function widgetViewPolicy(
  view: string,
  actor: WidgetViewPolicyActor,
  /** The row ACL of a workspace dashboard. Only an `entity-view:` key can
   *  carry one; for any other key it is ignored, as the grammar ignores it. */
  viewAcl?: WidgetViewAcl,
): WidgetViewPolicy {
  // The row ACL feeds the grammar the same way the server feeds it: read =
  // the viewer reaches the row, write = their level allows `edit`, and the
  // creator always writes (the server resolves them to full_edit, but the
  // compare here keeps a stale cached level from locking an owner out).
  const isOwner = viewAcl != null && viewAcl.ownerId === actor.userId;
  const aclCanEdit = isOwner || accessLevelCanEdit(viewAcl?.viewerAccessLevel);
  // The privacy bypass is server-only (no UI reaches a foreign private
  // view), so it's always false here.
  const { canWrite, ownership } = widgetViewAccess(view, {
    userId: actor.userId,
    canManageWidgets: actor.canManageWidgets,
    managesTeam: actor.managesTeam,
    privacyBypass: false,
    ...(viewAcl
      ? {
          entityViewAccess: () => ({
            canRead: isOwner || viewAcl.viewerAccessLevel != null,
            canWrite: aclCanEdit,
          }),
        }
      : {}),
  });

  // A share-only viewer consumes every shared board, whatever grant the
  // grammar found for them (the server caps their level to `view` on the
  // same reading, so `canWrite` is already false there; the flag is what
  // strips the personalization every other non-writer keeps).
  const consume = (tier: WidgetViewTier) => actor.shareOnly && isSharedTier(tier);

  if (ownership.visibility === "private") {
    // The UI only ever renders the current user's own member view, so
    // `private-owner` is the only reachable private tier; a non-owner
    // private view (canWrite false) degrades to read-only shared just in
    // case.
    return canWrite
      ? { canWrite, writeGrant: null, tier: "private-owner", consumeOnly: false }
      : { canWrite, writeGrant: null, tier: "shared", consumeOnly: consume("shared") };
  }
  if (viewAcl && ownership.scope?.kind === "entity-view") {
    const writeGrant = !canWrite ? null : isOwner ? "owner" : "access-level";
    return { canWrite, writeGrant, tier: "acl", consumeOnly: consume("acl") };
  }
  if (canWrite) {
    const writeGrant =
      ownership.scope?.kind === "team" && actor.managesTeam(ownership.scope.entityId)
        ? "team-manager"
        : "permission";
    return { canWrite, writeGrant, tier: "shared", consumeOnly: consume("shared") };
  }
  return { canWrite, writeGrant: null, tier: "shared", consumeOnly: consume("shared") };
}
